import { InputNumberFloat, InputText } from "@components-common/forms";
import { useField } from "formik";
import { CustomInput } from "taltech-styleguide";

/**
 * Functional Component: OverridenInput
 *
 * This component renders an input field that can override a default value. It uses the `useField` hook from Formik to manage
 * the form state and handles changes to the override state.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.id - The ID for the form field.
 * @param {number} props.index - The index of the field in the form array.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the custom input for overriding default values and the input field based on the type.
 */
export const OverridenInput = ({ id, index }: any) => {
  const [field, helpers, meta] = useField(`${id}[${[index]}]`);
  const [fieldName, helpersName, metaName] = useField(`${id}[${[index]}].name`);
  const [fieldType, helpersType, metaType] = useField(`${id}[${[index]}].type`);
  const [fieldDefaultValue, helpersDefaultValue, metaDefaultValue] = useField(
    `${id}[${[index]}].defaultValue`
  );
  const [fieldValue, helpersValue, metaValue] = useField(
    `${id}[${[index]}].value`
  );

  /**
   * Handles the change event for the override checkbox.
   * Sets the value based on whether the field value is defined or not.
   */
  const handleOverrideChange = () => {
    if (fieldValue.value !== undefined) {
      meta.setValue({
        id: field.value.id,
        name: fieldName.value,
        type: fieldType.value,
        defaultValue: fieldDefaultValue.value,
      });
    } else {
      meta.setValue({
        id: field.value.id,
        name: fieldName.value,
        type: fieldType.value,
        defaultValue: fieldDefaultValue.value,
        value: fieldType.value === "NUMBER" ? 0 : "",
      });
    }
  };

  return (
    <div className="d-flex align-items-center gap-3 w-100">
      <CustomInput
        checked={fieldValue.value !== undefined}
        onChange={handleOverrideChange}
      />
      {fieldType.value === "NUMBER" ? (
        <InputNumberFloat
          id={
            fieldValue.value !== undefined
              ? fieldValue.name
              : fieldDefaultValue.name
          }
          disabled={fieldValue.value === undefined}
          fixedDecimalScale
          decimalScale={3}
          showLabel={false}
          maxValue={9999}
          elementClass="w-100"
        />
      ) : (
        <InputText
          id={
            fieldValue.value !== undefined
              ? fieldValue.name
              : fieldDefaultValue.name
          }
          disabled={fieldValue.value === undefined}
          showLabel={false}
          elementClass="w-100"
        />
      )}
    </div>
  );
};
